import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useWorkspace } from '../context/WorkspaceContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import { HelpPanel, GuidedCallout, HelpTooltip } from '../components/ui/help-panel';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  ArrowLeft,
  CheckCircle2,
  Clock,
  AlertCircle,
  Plus,
  Save,
  Lock,
  Printer,
  FileText,
  Trash2,
  X,
  CircleDollarSign,
  TrendingUp,
  TrendingDown,
  Building2,
  Calendar,
  RefreshCw,
  Cloud,
  CloudOff,
  HelpCircle,
  FolderInput,
  Sparkles,
  Info,
  Loader2,
} from 'lucide-react';
import { cn } from '../lib/utils';
import { toast } from 'sonner';
import { format } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Local storage key for first-time tour
const TOUR_STORAGE_KEY = 'ledger_recon_tour_seen';

// Format currency
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(amount);
};

// Reconciliation Help Content
const RECON_HELP_SECTIONS = [
  {
    heading: "What does 'Cleared' mean?",
    content: "A cleared item is a transaction that appears on both your bank statement AND in your accounting records. When you mark something as cleared, you're confirming that the bank has processed this transaction and it matches what you recorded."
  },
  {
    heading: "What does 'Outstanding' mean?",
    content: "An outstanding item is a transaction that's in your accounting records but hasn't appeared on your bank statement yet. Common examples include checks you've written that haven't been cashed, or deposits you've recorded that the bank hasn't processed yet."
  },
  {
    heading: "How do outstanding items work across periods?",
    content: "Outstanding items automatically roll forward to your next reconciliation. When they finally appear on a future bank statement, you'll mark them as cleared then. This is normal—checks can take days or weeks to clear."
  },
  {
    heading: "What about items from Statement Upload?",
    content: "If you uploaded a bank statement and used Smart Reconcile to match transactions, those approved matches are automatically pre-populated as cleared items here. You can still adjust them if needed."
  },
  {
    heading: "When should the difference be zero?",
    type: "info",
    content: "Your reconciliation is complete when the 'Difference' equals $0.00. This means your adjusted book balance matches your bank statement balance. If there's still a difference, look for: missing transactions, duplicate entries, or items marked incorrectly."
  }
];

const RECON_HELP_TIPS = [
  "Start by marking obvious matches as cleared—deposits and large payments are usually easy to identify.",
  "If an item doesn't appear on your statement, mark it as outstanding—don't delete it.",
  "Bank fees and interest often need to be added as adjustments if you haven't recorded them yet.",
  "Use the Complete & Lock button only when you're confident everything is correct."
];

const RECON_HELP_WARNINGS = [
  "Once completed, a reconciliation is locked and can only be undone by voiding it.",
  "Never mark an item as cleared just to make the numbers balance—investigate discrepancies first."
];

/**
 * BankReconciliation - Main reconciliation page with optimistic updates
 */
export default function BankReconciliation() {
  const { reconciliationId } = useParams();
  const navigate = useNavigate();
  const { currentWorkspace } = useWorkspace();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  
  // First-time guided tour state
  const [showTour, setShowTour] = useState(false);
  const [tourStep, setTourStep] = useState(1);
  
  // Core reconciliation data from backend
  const [reconciliation, setReconciliation] = useState(null);
  
  // Local state for optimistic updates (single source of truth for UI)
  const [clearedIds, setClearedIds] = useState(new Set());
  const [outstandingIds, setOutstandingIds] = useState(new Set());
  
  const [activeTab, setActiveTab] = useState('debits');
  
  // Adjustment dialog state
  const [showAdjustmentDialog, setShowAdjustmentDialog] = useState(false);
  const [adjustmentType, setAdjustmentType] = useState('bank_fee');
  const [adjustmentAmount, setAdjustmentAmount] = useState('');
  const [adjustmentDescription, setAdjustmentDescription] = useState('');
  const [addingAdjustment, setAddingAdjustment] = useState(false);
  
  // Void dialog state
  const [showVoidDialog, setShowVoidDialog] = useState(false);
  // Auto-Reconcile with AI (Claude) — dialog with confidence-scored matches.
  const [showAutoMatchDialog, setShowAutoMatchDialog] = useState(false);
  const [autoMatchLoading, setAutoMatchLoading] = useState(false);
  const [autoMatchResult, setAutoMatchResult] = useState(null);  // { matches: [...], unmatched: [...] }
  const [autoMatchApplying, setAutoMatchApplying] = useState(false);
  const [selectedAutoMatches, setSelectedAutoMatches] = useState(new Set());
  // Trust-mode: per-workspace preference. When true, the next AI run auto-
  // applies high-confidence matches and only surfaces medium/low for review.
  // Hydrated from /reconciliation-settings on mount.
  const [autoTrustHigh, setAutoTrustHigh] = useState(false);
  const [autoTrustSaving, setAutoTrustSaving] = useState(false);
  // Reconcile help banner — dismissible; remembers the user's choice locally.
  const [showReconHelp, setShowReconHelp] = useState(() => {
    try { return window.localStorage.getItem('recon_help_dismissed') !== '1'; }
    catch { return true; }
  });
  const [voidReason, setVoidReason] = useState('');
  const [voiding, setVoiding] = useState(false);
  
  // Complete dialog state
  const [showCompleteDialog, setShowCompleteDialog] = useState(false);
  const [completing, setCompleting] = useState(false);

  // Fetch reconciliation details
  const fetchReconciliation = useCallback(async () => {
    if (!reconciliationId || !currentWorkspace) return;
    
    try {
      const response = await fetch(
        `${API_URL}/api/banking/reconciliations/${reconciliationId}?workspace_id=${currentWorkspace.workspace_id}`
      );
      
      if (response.ok) {
        const data = await response.json();
        setReconciliation(data);
        
        // Initialize local state from backend data
        const cleared = new Set(
          [...(data.debits || []), ...(data.credits || [])]
            .filter(i => i.status === 'cleared')
            .map(i => i.transaction_id)
        );
        const outstanding = new Set(
          [...(data.debits || []), ...(data.credits || [])]
            .filter(i => i.status === 'outstanding')
            .map(i => i.transaction_id)
        );
        setClearedIds(cleared);
        setOutstandingIds(outstanding);
        setLastSaved(data.last_saved_at);
      } else {
        toast.error('Failed to load reconciliation');
        navigate('/banking');
      }
    } catch (error) {
      toast.error('Failed to load reconciliation');
    } finally {
      setLoading(false);
    }
  }, [reconciliationId, currentWorkspace, navigate]);

  useEffect(() => {
    fetchReconciliation();
  }, [fetchReconciliation]);

  // Pull the workspace-level Auto-Reconcile trust preference on mount.
  useEffect(() => {
    if (!currentWorkspace?.workspace_id) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(
          `${API_URL}/api/workspaces/${currentWorkspace.workspace_id}/reconciliation-settings`,
        );
        if (!r.ok) return;
        const data = await r.json();
        if (!cancelled) setAutoTrustHigh(!!data.auto_clear_high_confidence_recon);
      } catch { /* silent — defaults to false */ }
    })();
    return () => { cancelled = true; };
  }, [currentWorkspace?.workspace_id]);

  // Check for first-time tour
  useEffect(() => {
    if (!loading && reconciliation && reconciliation.status === 'in_progress') {
      const hasSeenTour = localStorage.getItem(TOUR_STORAGE_KEY);
      if (!hasSeenTour) {
        setShowTour(true);
        setTourStep(1);
      }
    }
  }, [loading, reconciliation]);

  // Handle tour dismissal
  const dismissTourStep = () => {
    if (tourStep < 3) {
      setTourStep(tourStep + 1);
    } else {
      setShowTour(false);
      localStorage.setItem(TOUR_STORAGE_KEY, 'true');
    }
  };

  // Compute transaction lists with current status from local state
  const { debits, credits } = useMemo(() => {
    if (!reconciliation) return { debits: [], credits: [] };
    
    const computeStatus = (item) => {
      if (clearedIds.has(item.transaction_id)) return 'cleared';
      if (outstandingIds.has(item.transaction_id)) return 'outstanding';
      return 'unreconciled';
    };
    
    return {
      debits: (reconciliation.debits || []).map(item => ({
        ...item,
        status: computeStatus(item)
      })),
      credits: (reconciliation.credits || []).map(item => ({
        ...item,
        status: computeStatus(item)
      }))
    };
  }, [reconciliation, clearedIds, outstandingIds]);

  // Compute summary totals from local state (single source of truth)
  const summary = useMemo(() => {
    if (!reconciliation) return null;
    
    const outstandingDebitTotal = debits
      .filter(d => d.status === 'outstanding')
      .reduce((sum, d) => sum + d.amount, 0);
    
    const outstandingCreditTotal = credits
      .filter(c => c.status === 'outstanding')
      .reduce((sum, c) => sum + c.amount, 0);
    
    const adjustmentsTotal = (reconciliation.adjustments || [])
      .reduce((sum, adj) => sum + (adj.amount || 0), 0);
    
    // GL Balance - Outstanding Debits + Outstanding Credits + Adjustments
    const adjustedBalance = reconciliation.gl_balance - outstandingDebitTotal + outstandingCreditTotal + adjustmentsTotal;
    const difference = reconciliation.statement_balance - adjustedBalance;
    
    return {
      statementBalance: reconciliation.statement_balance,
      glBalance: reconciliation.gl_balance,
      adjustedBalance,
      difference: Math.round(difference * 100) / 100,
      outstandingDebitCount: debits.filter(d => d.status === 'outstanding').length,
      outstandingCreditCount: credits.filter(c => c.status === 'outstanding').length,
      outstandingDebitTotal,
      outstandingCreditTotal,
      adjustmentsTotal,
      clearedCount: clearedIds.size
    };
  }, [reconciliation, debits, credits, clearedIds]);

  // Autosave to backend (debounced via effect)
  const saveToBackend = useCallback(async () => {
    if (!reconciliation || reconciliation.status !== 'in_progress') return;
    
    setSaving(true);
    setSaveError(false);
    
    try {
      const response = await fetch(
        `${API_URL}/api/banking/reconciliations/${reconciliationId}?workspace_id=${currentWorkspace.workspace_id}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            cleared_transaction_ids: Array.from(clearedIds),
            outstanding_transaction_ids: Array.from(outstandingIds)
          })
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setLastSaved(data.last_saved_at);
      } else {
        setSaveError(true);
        toast.error('Failed to save - please try again');
      }
    } catch (error) {
      setSaveError(true);
      toast.error('Failed to save - please try again');
    } finally {
      setSaving(false);
    }
  }, [reconciliationId, currentWorkspace, reconciliation, clearedIds, outstandingIds]);

  // Handle toggle with optimistic update
  const handleToggleItem = useCallback((transactionId, currentStatus) => {
    if (!reconciliation || reconciliation.status !== 'in_progress') return;
    
    // Optimistic update - update local state immediately
    if (currentStatus === 'cleared') {
      // cleared -> outstanding
      setClearedIds(prev => {
        const next = new Set(prev);
        next.delete(transactionId);
        return next;
      });
      setOutstandingIds(prev => {
        const next = new Set(prev);
        next.add(transactionId);
        return next;
      });
    } else if (currentStatus === 'outstanding') {
      // outstanding -> unreconciled
      setOutstandingIds(prev => {
        const next = new Set(prev);
        next.delete(transactionId);
        return next;
      });
    } else {
      // unreconciled -> cleared
      setClearedIds(prev => {
        const next = new Set(prev);
        next.add(transactionId);
        return next;
      });
    }
  }, [reconciliation]);

  // Autosave when clearedIds or outstandingIds change (with debounce)
  useEffect(() => {
    if (!reconciliation || reconciliation.status !== 'in_progress') return;
    
    const timeoutId = setTimeout(() => {
      saveToBackend();
    }, 500); // 500ms debounce
    
    return () => clearTimeout(timeoutId);
  }, [clearedIds, outstandingIds, saveToBackend, reconciliation]);

  // Handle adding adjustment
  const handleAddAdjustment = async () => {
    if (!adjustmentAmount || !adjustmentDescription) {
      toast.error('Please fill in all fields');
      return;
    }
    
    const amount = parseFloat(adjustmentAmount);
    if (isNaN(amount) || amount === 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    
    // Bank fee should be negative, interest should be positive
    const finalAmount = adjustmentType === 'bank_fee' ? -Math.abs(amount) : 
                        adjustmentType === 'interest_income' ? Math.abs(amount) : amount;
    
    setAddingAdjustment(true);
    try {
      const response = await fetch(
        `${API_URL}/api/banking/reconciliations/${reconciliationId}/adjustments?workspace_id=${currentWorkspace.workspace_id}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            adjustment_type: adjustmentType,
            amount: finalAmount,
            description: adjustmentDescription
          })
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`Adjustment created: JE #${data.entry_no}`);
        setShowAdjustmentDialog(false);
        setAdjustmentAmount('');
        setAdjustmentDescription('');
        fetchReconciliation(); // Refresh to get new adjustment
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create adjustment');
      }
    } catch (error) {
      toast.error('Failed to create adjustment');
    } finally {
      setAddingAdjustment(false);
    }
  };

  // Handle completing reconciliation
  const handleComplete = async () => {
    setCompleting(true);
    try {
      const response = await fetch(
        `${API_URL}/api/banking/reconciliations/${reconciliationId}/complete?workspace_id=${currentWorkspace.workspace_id}`,
        {
          method: 'POST'
        }
      );
      
      if (response.ok) {
        toast.success('Reconciliation completed successfully!');
        setShowCompleteDialog(false);
        fetchReconciliation(); // Refresh to get completed status
      } else {
        const error = await response.json();
        if (error.detail?.error_code === 'reconciliation_not_balanced') {
          toast.error(error.detail.message);
        } else {
          toast.error(error.detail || 'Failed to complete reconciliation');
        }
      }
    } catch (error) {
      toast.error('Failed to complete reconciliation');
    } finally {
      setCompleting(false);
    }
  };

  // Handle voiding reconciliation
  const handleVoid = async () => {
    if (voidReason.length < 5) {
      toast.error('Please provide a reason (minimum 5 characters)');
      return;
    }
    
    setVoiding(true);
    try {
      const response = await fetch(
        `${API_URL}/api/banking/reconciliations/${reconciliationId}/void?workspace_id=${currentWorkspace.workspace_id}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ reason: voidReason })
        }
      );
      
      if (response.ok) {
        toast.success('Reconciliation voided');
        setShowVoidDialog(false);
        fetchReconciliation();
      } else {
        toast.error('Failed to void reconciliation');
      }
    } catch (error) {
      toast.error('Failed to void reconciliation');
    } finally {
      setVoiding(false);
    }
  };

  // Handle discard (in-progress only)
  const handleDiscard = async () => {
    if (!confirm('Are you sure you want to discard this reconciliation? This cannot be undone.')) return;
    
    try {
      const response = await fetch(
        `${API_URL}/api/banking/reconciliations/${reconciliationId}?workspace_id=${currentWorkspace.workspace_id}`,
        {
          method: 'DELETE'
        }
      );
      
      if (response.ok) {
        toast.success('Reconciliation discarded');
        navigate('/banking');
      } else {
        toast.error('Failed to discard reconciliation');
      }
    } catch (error) {
      toast.error('Failed to discard reconciliation');
    }
  };

  // Handle print
  const handlePrint = () => {
    window.print();
  };

  // Save the completed reconciliation as a PDF in the Documents Hub
  // under /Banking & Statements/Reconciliations/<Account>/.
  const [savingToDocs, setSavingToDocs] = useState(false);
  const handleSaveToDocuments = async () => {
    setSavingToDocs(true);
    try {
      const r = await fetch(
        `${API_URL}/api/banking/reconciliations/${reconciliationId}/save-to-documents`,
        { method: 'POST' },
      );
      const data = await r.json();
      if (!r.ok) {
        toast.error(data.detail || 'Failed to save to Documents');
        return;
      }
      toast.success('Saved to Documents Hub', {
        action: {
          label: 'Open',
          onClick: () => navigate(`/documents?doc=${data.document_id}`),
        },
      });
    } catch {
      toast.error('Failed to save to Documents');
    } finally {
      setSavingToDocs(false);
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto p-6 max-w-6xl">
        <Skeleton className="h-8 w-64 mb-6" />
        <div className="grid grid-cols-3 gap-4 mb-6">
          <Skeleton className="h-24" />
          <Skeleton className="h-24" />
          <Skeleton className="h-24" />
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  if (!reconciliation || !summary) {
    return (
      <div className="container mx-auto p-6 max-w-6xl">
        <p className="text-muted-foreground">Reconciliation not found</p>
        <Button variant="outline" onClick={() => navigate('/banking')} className="mt-4">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Banking
        </Button>
      </div>
    );
  }

  const isInProgress = reconciliation.status === 'in_progress';
  const isCompleted = reconciliation.status === 'completed';
  const isVoided = reconciliation.status === 'voided';
  const canComplete = isInProgress && Math.abs(summary.difference) < 0.01;

  return (
    <div className="container mx-auto p-6 max-w-6xl print:p-0" data-testid="bank-reconciliation-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 print:mb-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/banking')} className="print:hidden">
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Building2 className="w-6 h-6" />
              Bank Reconciliation
              {/* Help Panel Trigger */}
              <HelpPanel
                title="Bank Reconciliation Help"
                description="Learn how to reconcile your bank accounts step by step"
                sections={RECON_HELP_SECTIONS}
                tips={RECON_HELP_TIPS}
                warnings={RECON_HELP_WARNINGS}
                className="ml-1"
              />
            </h1>
            <p className="text-sm text-muted-foreground">
              {reconciliation.bank_name} {reconciliation.bank_mask && `****${reconciliation.bank_mask}`}
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2 print:hidden">
          {/* Status Badge */}
          {isInProgress && (
            <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
              <Clock className="w-3 h-3 mr-1" />
              In Progress
            </Badge>
          )}
          {isCompleted && (
            <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
              <CheckCircle2 className="w-3 h-3 mr-1" />
              Completed
            </Badge>
          )}
          {isVoided && (
            <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
              <X className="w-3 h-3 mr-1" />
              Voided
            </Badge>
          )}
          
          {/* Actions */}
          {isInProgress && (
            <>
              <Button variant="outline" size="sm" onClick={handleDiscard}>
                <Trash2 className="w-4 h-4 mr-1" />
                Discard
              </Button>
              <Button 
                size="sm" 
                onClick={() => setShowCompleteDialog(true)}
                disabled={!canComplete}
              >
                <Lock className="w-4 h-4 mr-1" />
                Complete & Lock
              </Button>
            </>
          )}
          {isCompleted && (
            <>
              <Button variant="outline" size="sm" onClick={handlePrint}>
                <Printer className="w-4 h-4 mr-1" />
                Print
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleSaveToDocuments}
                disabled={savingToDocs}
                data-testid="save-recon-to-documents-btn"
              >
                <FolderInput className="w-4 h-4 mr-1" />
                {savingToDocs ? 'Saving…' : 'Save to Documents'}
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => setShowVoidDialog(true)}
                className="text-red-600 hover:text-red-700"
              >
                Void
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Period Info + Autosave Status */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6 text-sm">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-muted-foreground" />
                <span className="text-muted-foreground">Period:</span>
                <span className="font-medium">
                  {format(new Date(reconciliation.start_date), 'MMM d, yyyy')} - {format(new Date(reconciliation.end_date), 'MMM d, yyyy')}
                </span>
              </div>
              {reconciliation.gl_account_name && (
                <div className="flex items-center gap-2">
                  <CircleDollarSign className="w-4 h-4 text-muted-foreground" />
                  <span className="text-muted-foreground">GL Account:</span>
                  <span className="font-medium">{reconciliation.gl_account_name}</span>
                </div>
              )}
            </div>
            
            {/* Autosave Status Indicator */}
            {isInProgress && (
              <div className="flex items-center gap-2 text-xs">
                {saving ? (
                  <span className="flex items-center gap-1.5 text-muted-foreground">
                    <RefreshCw className="w-3 h-3 animate-spin" />
                    Saving...
                  </span>
                ) : saveError ? (
                  <span className="flex items-center gap-1.5 text-red-600">
                    <CloudOff className="w-3 h-3" />
                    Save failed
                  </span>
                ) : (
                  <span className="flex items-center gap-1.5 text-emerald-600">
                    <Cloud className="w-3 h-3" />
                    Changes saved automatically
                    {lastSaved && (
                      <span className="text-muted-foreground ml-1">
                        • {format(new Date(lastSaved), 'h:mm a')}
                      </span>
                    )}
                  </span>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Reconciliation Summary - Always computed from local state */}
      <Card className="mb-6">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg">Reconciliation Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            <div className="p-4 bg-muted/30 rounded-lg">
              <p className="text-xs text-muted-foreground mb-1">Statement Balance</p>
              <p className="text-xl font-bold font-mono">{formatCurrency(summary.statementBalance)}</p>
            </div>
            <div className="p-4 bg-muted/30 rounded-lg">
              <p className="text-xs text-muted-foreground mb-1">GL Book Balance</p>
              <p className="text-xl font-bold font-mono">{formatCurrency(summary.glBalance)}</p>
            </div>
            <div className="p-4 bg-muted/30 rounded-lg">
              <p className="text-xs text-muted-foreground mb-1">Adjusted Balance</p>
              <p className="text-xl font-bold font-mono">{formatCurrency(summary.adjustedBalance)}</p>
            </div>
            {/* Difference with Guided Callout */}
            <GuidedCallout
              show={showTour && tourStep === 3}
              title="Difference Must Be Zero"
              description="When this equals $0.00, your books match the bank and you can complete the reconciliation."
              position="top"
              step={3}
              totalSteps={3}
              onDismiss={dismissTourStep}
            >
              <div className={cn(
                "p-4 rounded-lg",
                Math.abs(summary.difference) < 0.01 
                  ? "bg-emerald-50 dark:bg-emerald-950/30" 
                  : "bg-red-50 dark:bg-red-950/30"
              )}>
                <p className="text-xs text-muted-foreground mb-1">Difference</p>
                <p className={cn(
                  "text-xl font-bold font-mono",
                  Math.abs(summary.difference) < 0.01 
                    ? "text-emerald-600" 
                    : "text-red-600"
                )}>
                  {formatCurrency(summary.difference)}
                  {Math.abs(summary.difference) < 0.01 && (
                    <CheckCircle2 className="w-5 h-5 inline ml-2 text-emerald-600" />
                  )}
                </p>
              </div>
            </GuidedCallout>
          </div>
          
          {/* Breakdown */}
          <div className="text-sm space-y-1 p-4 bg-muted/20 rounded-lg font-mono">
            <div className="flex justify-between">
              <span>GL Book Balance</span>
              <span>{formatCurrency(summary.glBalance)}</span>
            </div>
            <div className="flex justify-between text-muted-foreground">
              <span>− Outstanding Payments ({summary.outstandingDebitCount})</span>
              <span>({formatCurrency(summary.outstandingDebitTotal)})</span>
            </div>
            <div className="flex justify-between text-muted-foreground">
              <span>+ Deposits in Transit ({summary.outstandingCreditCount})</span>
              <span>{formatCurrency(summary.outstandingCreditTotal)}</span>
            </div>
            {summary.adjustmentsTotal !== 0 && (
              <div className="flex justify-between text-muted-foreground">
                <span>{summary.adjustmentsTotal > 0 ? '+' : '−'} Adjustments</span>
                <span>{formatCurrency(Math.abs(summary.adjustmentsTotal))}</span>
              </div>
            )}
            <Separator className="my-2" />
            <div className="flex justify-between font-medium">
              <span>= Adjusted Book Balance</span>
              <span>{formatCurrency(summary.adjustedBalance)}</span>
            </div>
            <div className="flex justify-between">
              <span>Statement Balance</span>
              <span>{formatCurrency(summary.statementBalance)}</span>
            </div>
            <Separator className="my-2" />
            <div className={cn(
              "flex justify-between font-bold",
              Math.abs(summary.difference) < 0.01 ? "text-emerald-600" : "text-red-600"
            )}>
              <span>Difference</span>
              <span>{formatCurrency(summary.difference)}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Transaction Tabs */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <CardTitle className="text-lg">Transactions</CardTitle>
            {isInProgress && (
              <div className="flex items-center gap-2">
                <Button
                  variant="default"
                  size="sm"
                  onClick={() => setShowAutoMatchDialog(true)}
                  data-testid="auto-reconcile-btn"
                  className="bg-gradient-to-r from-violet-500 to-indigo-500 hover:from-violet-600 hover:to-indigo-600 text-white shadow-[0_0_12px_rgba(139,92,246,0.35)]"
                >
                  <Sparkles className="w-4 h-4 mr-1" />
                  Auto-Reconcile with AI
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowAdjustmentDialog(true)}
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Adjustment
                </Button>
              </div>
            )}
          </div>

          {/* In-line explainer — quick refresher on what to do here. We
              keep it dismissible so power users can hide it after a few
              reconciliations. Stored locally so it persists per browser. */}
          {isInProgress && showReconHelp && (
            <div className="mt-3 rounded-lg border border-blue-500/30 bg-blue-500/[0.06] p-3 text-xs text-foreground/90 leading-relaxed" data-testid="recon-help-banner">
              <div className="flex items-start gap-3">
                <Info className="w-4 h-4 text-blue-400 mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-foreground mb-1">How to reconcile</p>
                  <ol className="list-decimal list-outside ml-4 space-y-0.5 text-muted-foreground">
                    <li>Open your bank statement (paper or PDF).</li>
                    <li>For each line on the statement, find the matching transaction below and mark it <span className="text-emerald-400 font-semibold">Cleared</span>.</li>
                    <li>Items in your books that are NOT on the statement stay as <span className="text-amber-400 font-semibold">Outstanding</span> (uncashed checks, pending deposits).</li>
                    <li>Items on the statement that are NOT in your books → click <span className="text-foreground font-semibold">Add Adjustment</span> to record bank fees, interest, etc.</li>
                    <li>You&apos;re done when <span className="text-foreground font-semibold">Difference = $0.00</span> in the summary above.</li>
                  </ol>
                  <p className="mt-2 text-blue-300/90">
                    💡 In a hurry? Click <span className="font-semibold">Auto-Reconcile with AI</span> above — Claude will match the obvious ones for you and let you review before locking in.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => { setShowReconHelp(false); try { window.localStorage.setItem('recon_help_dismissed', '1'); } catch { /* noop */ } }}
                  className="text-muted-foreground hover:text-foreground"
                  aria-label="Dismiss"
                  data-testid="recon-help-dismiss"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-3 mb-4">
              <TabsTrigger value="debits" className="gap-2">
                <TrendingDown className="w-4 h-4" />
                Payments ({debits.length})
              </TabsTrigger>
              <TabsTrigger value="credits" className="gap-2">
                <TrendingUp className="w-4 h-4" />
                Deposits ({credits.length})
              </TabsTrigger>
              <TabsTrigger value="adjustments" className="gap-2">
                <FileText className="w-4 h-4" />
                Adjustments ({reconciliation.adjustments?.length || 0})
              </TabsTrigger>
            </TabsList>
            
            {/* Payments/Debits Tab */}
            <TabsContent value="debits">
              <TransactionList
                items={debits}
                isDebit={true}
                isEditable={isInProgress}
                onToggle={handleToggleItem}
                showTour={showTour}
                tourStep={tourStep}
                onDismissTour={dismissTourStep}
              />
            </TabsContent>
            
            {/* Deposits/Credits Tab */}
            <TabsContent value="credits">
              <TransactionList
                items={credits}
                isDebit={false}
                isEditable={isInProgress}
                onToggle={handleToggleItem}
              />
            </TabsContent>
            
            {/* Adjustments Tab */}
            <TabsContent value="adjustments">
              {reconciliation.adjustments?.length > 0 ? (
                <div className="space-y-2">
                  {reconciliation.adjustments.map((adj) => (
                    <div
                      key={adj.adjustment_id}
                      className="flex items-center justify-between p-3 bg-muted/30 rounded-lg"
                    >
                      <div>
                        <p className="font-medium">{adj.description}</p>
                        <p className="text-xs text-muted-foreground">
                          {adj.adjustment_type.replace('_', ' ')} • JE #{adj.entry_no} • {adj.date}
                        </p>
                      </div>
                      <span className={cn(
                        "font-mono font-medium",
                        adj.amount >= 0 ? "text-emerald-600" : "text-red-600"
                      )}>
                        {formatCurrency(adj.amount)}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>No adjustments added</p>
                  {isInProgress && (
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="mt-2"
                      onClick={() => setShowAdjustmentDialog(true)}
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Add Adjustment
                    </Button>
                  )}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Void info if voided */}
      {isVoided && reconciliation.void_reason && (
        <Card className="mt-6 border-red-200 bg-red-50/50 dark:bg-red-950/20">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-red-700 dark:text-red-400">This reconciliation was voided</p>
                <p className="text-sm text-red-600/80 dark:text-red-400/80 mt-1">
                  Reason: {reconciliation.void_reason}
                </p>
                <p className="text-xs text-muted-foreground mt-2">
                  Voided by {reconciliation.voided_by} on {format(new Date(reconciliation.voided_at), 'MMM d, yyyy h:mm a')}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Completion info if completed */}
      {isCompleted && (
        <Card className="mt-6 border-emerald-200 bg-emerald-50/50 dark:bg-emerald-950/20">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-emerald-700 dark:text-emerald-400">Reconciliation Complete</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Reconciled by {reconciliation.completed_by} on {format(new Date(reconciliation.completed_at), 'MMM d, yyyy h:mm a')}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Add Adjustment Dialog */}
      <Dialog open={showAdjustmentDialog} onOpenChange={setShowAdjustmentDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Adjustment</DialogTitle>
            <DialogDescription>
              Create a journal entry adjustment for this reconciliation.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Adjustment Type</Label>
              <Select value={adjustmentType} onValueChange={setAdjustmentType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="bank_fee">Bank Fee (Expense)</SelectItem>
                  <SelectItem value="interest_income">Interest Income</SelectItem>
                  <SelectItem value="adjustment">Other Adjustment</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Amount</Label>
              <Input
                type="number"
                step="0.01"
                placeholder="0.00"
                value={adjustmentAmount}
                onChange={(e) => setAdjustmentAmount(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                {adjustmentType === 'bank_fee' 
                  ? 'Enter as positive (will be recorded as expense)' 
                  : adjustmentType === 'interest_income'
                    ? 'Enter as positive (will be recorded as income)'
                    : 'Positive = increase cash, Negative = decrease cash'}
              </p>
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Input
                placeholder="e.g., Monthly service fee"
                value={adjustmentDescription}
                onChange={(e) => setAdjustmentDescription(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAdjustmentDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddAdjustment} disabled={addingAdjustment}>
              {addingAdjustment ? 'Creating...' : 'Create Adjustment'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Complete Confirmation Dialog */}
      <AlertDialog open={showCompleteDialog} onOpenChange={setShowCompleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Complete Reconciliation?</AlertDialogTitle>
            <AlertDialogDescription>
              This will lock all reconciled transactions. Once completed, you can only make changes by voiding this reconciliation and starting over.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleComplete} disabled={completing}>
              {completing ? 'Completing...' : 'Complete & Lock'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Void Dialog */}
      <Dialog open={showVoidDialog} onOpenChange={setShowVoidDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Void Reconciliation</DialogTitle>
            <DialogDescription>
              This will unlock all transactions and allow them to be modified. Adjustment entries will remain but will no longer be locked.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label>Reason for voiding (required)</Label>
            <Input
              className="mt-2"
              placeholder="e.g., Found missing transactions"
              value={voidReason}
              onChange={(e) => setVoidReason(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowVoidDialog(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleVoid} 
              disabled={voiding || voidReason.length < 5}
            >
              {voiding ? 'Voiding...' : 'Void Reconciliation'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Auto-Reconcile with Claude — confidence-scored matches the user
          can approve in bulk. We never auto-apply without consent. */}
      <Dialog
        open={showAutoMatchDialog}
        onOpenChange={(open) => {
          if (!open) {
            setShowAutoMatchDialog(false);
            setAutoMatchResult(null);
            setSelectedAutoMatches(new Set());
          }
        }}
      >
        <DialogContent className="max-w-3xl" data-testid="auto-match-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-violet-500" />
              Auto-Reconcile with AI
            </DialogTitle>
            <DialogDescription>
              Claude reviews the unreconciled transactions and surfaces likely matches to the bank statement. You approve before anything is locked.
            </DialogDescription>
          </DialogHeader>

          {!autoMatchResult && !autoMatchLoading && (
            <div className="space-y-4 py-2">
              <div className="rounded-lg border border-violet-500/30 bg-violet-500/[0.06] p-4 text-sm text-foreground/90 leading-relaxed">
                <p className="font-semibold mb-1">What this does</p>
                <ul className="list-disc list-outside ml-5 space-y-1 text-muted-foreground">
                  <li>Compares each book transaction to the statement period &amp; ending balance.</li>
                  <li>Returns high / medium / low confidence matches.</li>
                  <li>You pick which to clear — nothing is applied until you click Apply.</li>
                </ul>
              </div>

              {/* Trust-mode toggle. Saved per-workspace via PUT so it sticks
                  across devices. When ON, the next run silently applies
                  high-confidence matches and only shows medium/low rows. */}
              <label
                className="flex items-start gap-3 p-3 rounded-lg border border-border bg-muted/30 cursor-pointer hover:bg-muted/50"
                data-testid="auto-trust-toggle-row"
              >
                <Checkbox
                  checked={autoTrustHigh}
                  disabled={autoTrustSaving}
                  onCheckedChange={async (next) => {
                    setAutoTrustSaving(true);
                    const prev = autoTrustHigh;
                    setAutoTrustHigh(!!next);
                    try {
                      const r = await fetch(
                        `${API_URL}/api/workspaces/${currentWorkspace.workspace_id}/reconciliation-settings`,
                        {
                          method: 'PUT',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ auto_clear_high_confidence_recon: !!next }),
                        },
                      );
                      if (!r.ok) {
                        setAutoTrustHigh(prev);
                        toast.error('Failed to save preference');
                        return;
                      }
                      toast.success(next
                        ? 'Trust enabled — next AI run will auto-clear high-confidence matches'
                        : 'Trust disabled — you\'ll review every AI suggestion');
                    } finally { setAutoTrustSaving(false); }
                  }}
                  data-testid="auto-trust-toggle"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-foreground">
                    Trust this AI — auto-clear high-confidence matches
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5 leading-snug">
                    When enabled, Claude marks high-confidence matches as cleared on the spot.
                    You still review medium/low matches before they&apos;re applied.
                    Saved for this workspace; toggle anytime.
                  </p>
                </div>
              </label>

              <Button
                onClick={async () => {
                  setAutoMatchLoading(true);
                  try {
                    const res = await fetch(
                      `${API_URL}/api/banking/reconciliations/${reconciliationId}/auto-match`,
                      { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' },
                    );
                    const data = await res.json();
                    if (!res.ok) { toast.error(data.detail || 'Auto-match failed'); return; }
                    setAutoMatchResult(data);
                    if (data.trust_enabled && data.auto_applied_count > 0) {
                      // Server already cleared the high-confidence ones —
                      // refresh and tell the user. Medium/low (if any)
                      // remain in `data.matches` for review.
                      toast.success(`${data.auto_applied_count} high-confidence matches auto-cleared`);
                      fetchReconciliation();
                    }
                    // Pre-select all HIGH rows (unless they were just auto-applied).
                    const autoApplied = new Set(data.auto_applied_ids || []);
                    const high = new Set(
                      (data.matches || [])
                        .filter((m) => m.confidence === 'high' && !autoApplied.has(m.transaction_id))
                        .map((m) => m.transaction_id),
                    );
                    setSelectedAutoMatches(high);
                  } catch {
                    toast.error('Auto-match failed');
                  } finally {
                    setAutoMatchLoading(false);
                  }
                }}
                disabled={autoMatchLoading}
                className="bg-gradient-to-r from-violet-500 to-indigo-500 hover:from-violet-600 hover:to-indigo-600 text-white w-full"
                data-testid="auto-match-run-btn"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                Run AI match
              </Button>
            </div>
          )}

          {autoMatchLoading && (
            <div className="py-12 text-center" data-testid="auto-match-loading">
              <Loader2 className="w-8 h-8 mx-auto animate-spin text-violet-400 mb-3" />
              <p className="text-sm text-muted-foreground">Claude is reviewing the transactions…</p>
            </div>
          )}

          {autoMatchResult && (
            <div className="space-y-4">
              {autoMatchResult.trust_enabled && autoMatchResult.auto_applied_count > 0 && (
                <div
                  className="rounded-lg border border-emerald-500/40 bg-emerald-500/[0.08] p-3 text-sm text-emerald-200 flex items-start gap-2"
                  data-testid="auto-applied-banner"
                >
                  <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />                  <div>
                    <p className="font-semibold">
                      {autoMatchResult.auto_applied_count} high-confidence matches auto-cleared
                    </p>
                    <p className="text-xs text-emerald-300/80">
                      Trust mode is on for this workspace. Review the remaining medium/low matches below.
                    </p>
                  </div>
                </div>
              )}
              {autoMatchResult.summary && (
                <p className="text-sm italic text-muted-foreground border-l-2 border-violet-400/40 pl-3" data-testid="auto-match-summary">
                  &ldquo;{autoMatchResult.summary}&rdquo;
                </p>
              )}
              <div className="text-xs font-mono text-muted-foreground" data-testid="auto-match-meta">
                Reviewed {autoMatchResult.candidates_count} txns ·{' '}
                <span className="text-emerald-400">{autoMatchResult.high_confidence_count} high-confidence matches</span>
                {autoMatchResult.truncated && (
                  <span className="text-amber-400 ml-2">
                    · ⚠ {autoMatchResult.candidates_total - autoMatchResult.candidates_count} additional txns not reviewed (too many for one pass)
                  </span>
                )}
                {autoMatchResult.ai_parse_failed && (
                  <span className="text-amber-400 ml-2">· ⚠ AI response couldn&apos;t be parsed — try again or match manually</span>
                )}
              </div>
              <ScrollArea className="max-h-[50vh] pr-3">
                <div className="space-y-1">
                  {(() => {
                    const autoAppliedSet = new Set(autoMatchResult.auto_applied_ids || []);
                    const reviewable = (autoMatchResult.matches || []).filter(
                      (m) => !autoAppliedSet.has(m.transaction_id),
                    );
                    if (reviewable.length === 0) {
                      return (
                        <p className="text-sm text-muted-foreground py-6 text-center" data-testid="auto-match-empty-review">
                          {autoMatchResult.trust_enabled && autoMatchResult.auto_applied_count > 0
                            ? 'All high-confidence matches were auto-cleared. Nothing left to review.'
                            : 'No matches surfaced. Try matching manually or add an adjustment.'}
                        </p>
                      );
                    }
                    return reviewable.map((m) => {
                    const checked = selectedAutoMatches.has(m.transaction_id);
                    return (
                      <label
                        key={m.transaction_id}
                        className={cn(
                          'flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors',
                          checked ? 'border-emerald-500/40 bg-emerald-500/[0.08]' : 'border-border hover:bg-muted/40',
                        )}
                        data-testid={`auto-match-row-${m.transaction_id}`}
                      >
                        <Checkbox
                          checked={checked}
                          onCheckedChange={(v) => {
                            const next = new Set(selectedAutoMatches);
                            if (v) next.add(m.transaction_id);
                            else next.delete(m.transaction_id);
                            setSelectedAutoMatches(next);
                          }}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-medium text-foreground truncate">{m.description || 'Unknown'}</p>
                            <Badge
                              variant="outline"
                              className={cn(
                                'text-[10px] uppercase tracking-wider px-1.5 py-0',
                                m.confidence === 'high' && 'border-emerald-500/40 text-emerald-400',
                                m.confidence === 'medium' && 'border-amber-500/40 text-amber-400',
                                m.confidence === 'low' && 'border-zinc-500/40 text-zinc-400',
                              )}
                            >
                              {m.confidence}
                            </Badge>
                          </div>
                          {m.reason && <p className="text-xs text-muted-foreground truncate">{m.reason}</p>}
                          <p className="text-[11px] text-muted-foreground font-mono">{m.date}</p>
                        </div>
                        <span className="font-mono text-sm whitespace-nowrap">{formatCurrency(m.amount)}</span>
                      </label>
                    );
                  });
                  })()}
                </div>
              </ScrollArea>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowAutoMatchDialog(false); setAutoMatchResult(null); setSelectedAutoMatches(new Set()); }}>
              Cancel
            </Button>
            {autoMatchResult && (
              <Button
                disabled={autoMatchApplying || selectedAutoMatches.size === 0}
                onClick={async () => {
                  setAutoMatchApplying(true);
                  try {
                    const idsToClear = Array.from(selectedAutoMatches);
                    // Re-use the existing PATCH /reconciliations/{id} endpoint
                    // to add these IDs to cleared_transaction_ids without
                    // disturbing already-cleared items.
                    const currentCleared = new Set(reconciliation.cleared_transaction_ids || []);
                    idsToClear.forEach(id => currentCleared.add(id));
                    const res = await fetch(
                      `${API_URL}/api/banking/reconciliations/${reconciliationId}?workspace_id=${currentWorkspace.workspace_id}`,
                      {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ cleared_transaction_ids: Array.from(currentCleared) }),
                      },
                    );
                    if (!res.ok) {
                      const err = await res.json().catch(() => ({}));
                      toast.error(err.detail || 'Failed to apply matches');
                      return;
                    }
                    toast.success(`${idsToClear.length} transactions marked as cleared`);
                    setShowAutoMatchDialog(false);
                    setAutoMatchResult(null);
                    setSelectedAutoMatches(new Set());
                    fetchReconciliation();
                  } catch {
                    toast.error('Failed to apply matches');
                  } finally {
                    setAutoMatchApplying(false);
                  }
                }}
                className="bg-emerald-500 hover:bg-emerald-600 text-white"
                data-testid="auto-match-apply-btn"
              >
                {autoMatchApplying ? 'Applying…' : `Mark ${selectedAutoMatches.size} as cleared`}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Transaction List Component
function TransactionList({ items, isDebit, isEditable, onToggle, showTour, tourStep, onDismissTour }) {
  if (!items || items.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        {isDebit ? (
          <>
            <TrendingDown className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p>No payments in this period</p>
          </>
        ) : (
          <>
            <TrendingUp className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p>No deposits in this period</p>
          </>
        )}
      </div>
    );
  }

  return (
    <ScrollArea className="max-h-[65vh] sm:max-h-[70vh] pr-3" data-testid="recon-txn-scroll">
      <div className="space-y-1">
        {items.map((item, index) => {
          // Show guided callout only on first item when tour is active
          const showClearedCallout = showTour && tourStep === 1 && index === 0 && isEditable;
          const showOutstandingCallout = showTour && tourStep === 2 && index === 0 && isEditable;
          
          const itemContent = (
            <div
              key={item.transaction_id}
              className={cn(
                "flex items-center gap-3 p-3 rounded-lg transition-colors",
                item.status === 'cleared' && "bg-emerald-50/50 dark:bg-emerald-950/20",
                item.status === 'outstanding' && "bg-amber-50/50 dark:bg-amber-950/20",
                item.status === 'unreconciled' && "bg-muted/30",
                isEditable && "cursor-pointer hover:bg-muted/50"
              )}
              onClick={() => isEditable && onToggle(item.transaction_id, item.status)}
              data-testid={`recon-item-${item.transaction_id}`}
            >
              {isEditable && (
                <Checkbox
                  checked={item.status === 'cleared'}
                  className="pointer-events-none"
                />
              )}
              
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium truncate">{item.description}</span>
                  {item.is_previously_outstanding && (
                    <Badge variant="outline" className="text-[10px] px-1 py-0">
                      Was Outstanding
                    </Badge>
                  )}
                </div>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <span>{item.date}</span>
                  {!item.is_posted && (
                    <Badge variant="secondary" className="text-[10px]">Not Posted</Badge>
                  )}
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                {item.status === 'cleared' && (
                  <HelpTooltip content="This item appears on your bank statement">
                    <Badge variant="outline" className="bg-emerald-100 text-emerald-700 border-emerald-200">
                      <CheckCircle2 className="w-3 h-3 mr-1" />
                      Cleared
                    </Badge>
                  </HelpTooltip>
                )}
                {item.status === 'outstanding' && (
                  <HelpTooltip content="This item hasn't cleared the bank yet">
                    <Badge variant="outline" className="bg-amber-100 text-amber-700 border-amber-200">
                      <Clock className="w-3 h-3 mr-1" />
                      Outstanding
                    </Badge>
                  </HelpTooltip>
                )}
                <span className={cn(
                  "font-mono font-medium",
                  isDebit ? "text-red-600" : "text-emerald-600"
                )}>
                  {isDebit ? '-' : '+'}{formatCurrency(item.amount)}
                </span>
              </div>
            </div>
          );

          // Wrap first item with callouts if tour is active
          if (showClearedCallout) {
            return (
              <GuidedCallout
                key={item.transaction_id}
                show={true}
                title="Click to Mark as Cleared"
                description="Cleared means this item appears on your bank statement. Click the row or checkbox to toggle."
                position="right"
                step={1}
                totalSteps={3}
                onDismiss={onDismissTour}
              >
                {itemContent}
              </GuidedCallout>
            );
          }
          
          if (showOutstandingCallout) {
            return (
              <GuidedCallout
                key={item.transaction_id}
                show={true}
                title="Click Again for Outstanding"
                description="Outstanding means it's in your books but not yet on the bank statement. Click again to mark as outstanding."
                position="right"
                step={2}
                totalSteps={3}
                onDismiss={onDismissTour}
              >
                {itemContent}
              </GuidedCallout>
            );
          }

          return itemContent;
        })}
      </div>
    </ScrollArea>
  );
}
